import { useQueries } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../api/client";
import type { ReportOut } from "../api/types";
import { Icon } from "../components/Icon";
import { TaxonomyText } from "../components/TaxonomyText";
import { useLang } from "../stores/language";

export const MAX_COMPARED = 3;
const SKELETON_ROWS = 4;

// Stands in for a column whose report is still loading or being translated,
// so an empty cell never reads as "nothing here".
function SkeletonCell({ description, comment }: { description: boolean; comment: boolean }) {
  return (
    <td aria-busy="true">
      <span className="sr-only">Loading…</span>
      <div className="compare-badges" aria-hidden="true">
        <span className="skeleton skeleton-badge" />
        <span className="skeleton skeleton-badge" />
      </div>
      {description && (
        <div className="field-val" aria-hidden="true">
          <span className="skeleton skeleton-line" />
          <span className="skeleton skeleton-line" />
          <span className="skeleton skeleton-line skeleton-short" />
        </div>
      )}
      {comment && (
        <div className="field-val compare-comment" aria-hidden="true">
          <span className="skeleton skeleton-line" />
          <span className="skeleton skeleton-line skeleton-short" />
        </div>
      )}
    </td>
  );
}

// Client feedback: two or three leases side by side, one row per topic, to
// compare clauses and then shorten them for the report. Each column is that
// document's report as it stands, reviewed rows and unreviewed ones alike.
export function ComparePage() {
  const lang = useLang();
  const [searchParams] = useSearchParams();
  const docIds = (searchParams.get("docs") ?? "").split(",").filter(Boolean).slice(0, MAX_COMPARED);
  const [showDescription, setShowDescription] = useState(true);
  const [showComment, setShowComment] = useState(true);

  const documents = useQueries({
    queries: docIds.map((id) => ({ queryKey: ["document", id], queryFn: () => api.getDocument(id) })),
  });
  const analyses = useQueries({
    queries: docIds.map((id) => ({
      queryKey: ["latest-analysis", id],
      queryFn: () => api.latestAnalysis(id),
      retry: false,
    })),
  });
  const reports = useQueries({
    queries: analyses.map((analysis) => ({
      queryKey: ["report", analysis.data?.id, lang],
      queryFn: () => api.getReport(analysis.data!.id, lang),
      enabled: !!analysis.data,
      placeholderData: (previous: ReportOut | undefined) => previous,
    })),
  });

  if (docIds.length < 2) {
    return (
      <div className="card card-pad" style={{ margin: 24 }}>
        Select two or three analyses on the list to compare them.{" "}
        <Link to="/">All analyses</Link>
      </div>
    );
  }

  const loaded = reports.map((report) => report.data).filter((r): r is ReportOut => !!r);
  const columnState = (index: number): "ready" | "loading" | "no-analysis" | "failed" =>
    analyses[index].isError
      ? "no-analysis"
      : reports[index].data
        ? "ready"
        : reports[index].isError
          ? "failed"
          : "loading";
  const otherCell = (index: number, id: string) =>
    columnState(index) === "no-analysis" ? (
      <td key={id} className="card-sub">No analysis yet.</td>
    ) : columnState(index) === "failed" ? (
      <td key={id} className="card-sub">The report could not be loaded.</td>
    ) : (
      <SkeletonCell key={id} description={showDescription} comment={showComment} />
    );
  // Every document uses the same rubric, so the first loaded report gives the
  // row order; a row missing from another report shows as empty.
  const rowKeys = loaded[0]?.rows.map((row) => row.row_key) ?? [];

  return (
    <div className="compare-page">
      <div className="page-head">
        <div>
          <div className="row-eyebrow">Comparison</div>
          <h2 className="page-title">Compare analyses</h2>
          {reports.some((report) => report.isFetching) && loaded.length > 0 && (
            <div className="card-sub">
              <Icon name="refresh" size={12} className="spin" /> Translating…
            </div>
          )}
          {loaded.some((report) => report.machine_translated) && (
            <div className="card-sub">Model text is machine-translated.</div>
          )}
        </div>
        <div className="compare-toggles">
          <label>
            <input
              type="checkbox"
              checked={showDescription}
              onChange={(event) => setShowDescription(event.target.checked)}
            />{" "}
            Description
          </label>
          <label>
            <input
              type="checkbox"
              checked={showComment}
              onChange={(event) => setShowComment(event.target.checked)}
            />{" "}
            Report comment
          </label>
        </div>
      </div>

      <div className="compare-scroll">
        <table className="compare-table">
          <thead>
            <tr>
              <th>Analysis item</th>
              {docIds.map((id, index) => (
                <th key={id}>
                  {documents[index].data ? (
                    <Link to={`/docs/${id}/report`}>{documents[index].data.original_filename}</Link>
                  ) : (
                    <span className="skeleton skeleton-line" aria-label="Loading" />
                  )}
                  {documents[index].data?.project_name && (
                    <div className="card-sub">{documents[index].data?.project_name}</div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loaded.length === 0 &&
              (analyses.every((a) => a.isError) ? (
                <tr>
                  <td colSpan={docIds.length + 1}>None of these documents has an analysis yet.</td>
                </tr>
              ) : (
                // No report yet, so the row topics are unknown: a few placeholder rows.
                Array.from({ length: SKELETON_ROWS }, (_, rowIndex) => (
                  <tr key={rowIndex}>
                    <th scope="row">
                      <span className="skeleton skeleton-line" />
                      <span className="skeleton skeleton-line skeleton-short" />
                    </th>
                    {docIds.map((id, index) => otherCell(index, id))}
                  </tr>
                ))
              ))}
            {rowKeys.map((rowKey) => {
              const first = loaded[0].rows.find((row) => row.row_key === rowKey)!;
              return (
                <tr key={rowKey}>
                  <th scope="row">
                    {lang === "pl" ? first.subject_pl : first.subject_en}
                    <div className="card-sub">{lang === "pl" ? first.subject_en : first.subject_pl}</div>
                  </th>
                  {docIds.map((id, index) => {
                    const report = reports[index].data;
                    const row = report?.rows.find((candidate) => candidate.row_key === rowKey);
                    if (!report) return otherCell(index, id);
                    if (!row) return <td key={id} className="card-sub">—</td>;
                    return (
                      <td key={id}>
                        <div className="compare-badges">
                          <span className={`risk-banner risk-${row.risk_level}`}>
                            {row.risk_level}
                          </span>
                          <span className={`assessment-badge assessment-${row.assessment_status}`}>
                            {row.assessment_status.replaceAll("_", " ")}
                          </span>
                        </div>
                        {showDescription && (
                          <div className="field-val">
                            <TaxonomyText text={row.description} terms={report.terms} />
                          </div>
                        )}
                        {showComment && (
                          <div className="field-val compare-comment">
                            <TaxonomyText text={row.comments} terms={report.terms} />
                          </div>
                        )}
                        <div className="card-sub">
                          {row.source.label}
                          {row.source.pages.length > 0 &&
                            ` · Pages: ${row.source.pages.join(", ")}`}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
